import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Button } from './Button';
import { Camera } from './Icon';
import { submitVerification } from '../services/verification';
import { resizeImageIfNeeded, uploadVerificationPhoto } from '../services/verificationPhoto';

const MIN_HEIGHT_M = 1.5;
const MAX_HEIGHT_M = 4.0;
const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

type Tab = 'manual' | 'photo';
type PhotoStatus = 'idle' | 'uploading' | 'processing' | 'done' | 'error';

interface VerifyHeightDialogProps {
  open: boolean;
  parkingId: string;
  onClose: () => void;
  /** Called after a successful submit so the caller can refetch the parking. */
  onSubmitted: () => void;
  /** Which tab to open on ("I've measured it" vs "I have a photo" in the sheet). */
  initialTab?: Tab;
}

export const VerifyHeightDialog: React.FC<VerifyHeightDialogProps> = ({
  open,
  parkingId,
  onClose,
  onSubmitted,
  initialTab = 'manual',
}) => {
  const [tab, setTab] = useState<Tab>(initialTab);

  // Manual tab state
  const [heightStr, setHeightStr] = useState('');
  const [manualBusy, setManualBusy] = useState(false);
  const [manualError, setManualError] = useState<string | null>(null);
  const [manualConfirmation, setManualConfirmation] = useState<string | null>(null);

  // Photo tab state
  const [photoStatus, setPhotoStatus] = useState<PhotoStatus>('idle');
  const [photoProgress, setPhotoProgress] = useState(0);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [photoConfirmation, setPhotoConfirmation] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) return;
    setTab(initialTab);
    setHeightStr('');
    setManualBusy(false);
    setManualError(null);
    setManualConfirmation(null);
    setPhotoStatus('idle');
    setPhotoProgress(0);
    setPhotoError(null);
    setPhotoConfirmation(null);
  }, [open, initialTab]);

  if (!open) return null;

  const submitManual = async (e: React.FormEvent) => {
    e.preventDefault();
    const heightM = parseFloat(heightStr.replace(',', '.'));
    if (!Number.isFinite(heightM) || heightM < MIN_HEIGHT_M || heightM > MAX_HEIGHT_M) {
      setManualError(`Enter a height between ${MIN_HEIGHT_M} and ${MAX_HEIGHT_M} meters.`);
      return;
    }
    setManualBusy(true);
    setManualError(null);
    try {
      const result = await submitVerification({ parkingId, method: 'manual', heightM });
      setManualConfirmation(
        result.status === 'accepted'
          ? 'Thanks — recorded.'
          : 'Submitted for review — thanks for helping verify this garage.'
      );
      onSubmitted();
      setTimeout(onClose, 1400);
    } catch (err) {
      setManualError((err as Error).message ?? 'Something went wrong.');
    } finally {
      setManualBusy(false);
    }
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setPhotoError(null);
    setPhotoConfirmation(null);
    setPhotoStatus('uploading');
    setPhotoProgress(0);

    try {
      const blob = await resizeImageIfNeeded(file, MAX_UPLOAD_BYTES);
      const measurementId =
        typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : `m_${Date.now()}`;
      const { path, done } = uploadVerificationPhoto(parkingId, measurementId, blob, setPhotoProgress);
      await done;

      setPhotoStatus('processing');
      const result = await submitVerification({
        parkingId,
        method: 'photo',
        photoStoragePath: path,
      });

      setPhotoStatus('done');
      setPhotoConfirmation(
        result.status === 'accepted'
          ? 'Thanks — reading matched and was recorded.'
          : "Submitted for review — we couldn't auto-confirm the reading, so a moderator will take a look."
      );
      onSubmitted();
      setTimeout(onClose, 1800);
    } catch (err) {
      setPhotoStatus('error');
      setPhotoError((err as Error).message ?? 'Upload failed. Try again.');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/40"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Verify the height"
    >
      <div
        className="bg-card w-full md:max-w-sm rounded-t-xl md:rounded-lg p-s5 pb-s7 md:pb-s5 border border-rule"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="font-serif font-medium text-[18px] text-ink1 mb-1">
          What height did you measure?
        </div>
        <p className="text-[12px] text-ink3 font-sans mb-s4">
          Read the posted height sign at the entrance, or photograph it.
        </p>

        <div className="flex gap-1.5 mb-s4">
          <button
            type="button"
            onClick={() => setTab('manual')}
            className={clsx(
              'flex-1 py-2 rounded-pill text-[12px] font-sans font-medium border transition-colors',
              tab === 'manual' ? 'bg-ink1 border-ink1 text-card' : 'bg-page border-rule text-ink2'
            )}
          >
            I've measured it
          </button>
          <button
            type="button"
            onClick={() => setTab('photo')}
            className={clsx(
              'flex-1 py-2 rounded-pill text-[12px] font-sans font-medium border transition-colors',
              tab === 'photo' ? 'bg-ink1 border-ink1 text-card' : 'bg-page border-rule text-ink2'
            )}
          >
            I have a photo
          </button>
        </div>

        {tab === 'manual' ? (
          <form onSubmit={submitManual} className="flex flex-col gap-s4">
            <div className="bg-page border border-rule rounded-md px-s4 py-3.5 flex items-baseline justify-between">
              <input
                type="text"
                inputMode="decimal"
                value={heightStr}
                onChange={(e) => setHeightStr(e.target.value)}
                placeholder="1.85"
                disabled={manualBusy}
                autoFocus
                className="flex-1 bg-transparent font-mono text-[24px] text-ink1 placeholder-ink3 outline-none"
              />
              <span className="font-sans text-[11px] font-semibold text-ink3 uppercase tracking-widest">
                M
              </span>
            </div>

            {manualError ? (
              <div className="text-[13px] text-tier-reported font-sans">{manualError}</div>
            ) : null}
            {manualConfirmation ? (
              <div className="text-[13px] text-tier-verified font-sans">{manualConfirmation}</div>
            ) : null}

            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="secondary"
                className="flex-1"
                onClick={onClose}
                disabled={manualBusy}
              >
                Cancel
              </Button>
              <Button type="submit" className="flex-1" loading={manualBusy}>
                Submit
              </Button>
            </div>
          </form>
        ) : (
          <div className="flex flex-col gap-s4">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFileSelected}
              className="hidden"
            />

            {photoStatus === 'idle' || photoStatus === 'error' ? (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex flex-col items-center justify-center gap-2 py-s6 border border-dashed border-rule rounded-lg text-ink2"
              >
                <Camera size={28} color="#8B8680" />
                <span className="text-[13px] font-sans font-medium">Take or choose a photo</span>
              </button>
            ) : null}

            {photoStatus === 'uploading' ? (
              <div className="flex flex-col gap-2 items-center py-s5">
                <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-ink3 border-t-transparent" />
                <span className="text-[12px] text-ink3 font-sans">
                  Uploading… {Math.round(photoProgress * 100)}%
                </span>
              </div>
            ) : null}

            {photoStatus === 'processing' ? (
              <div className="flex flex-col gap-2 items-center py-s5">
                <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-ink3 border-t-transparent" />
                <span className="text-[12px] text-ink3 font-sans">Reading the sign…</span>
              </div>
            ) : null}

            {photoError ? (
              <div className="text-[13px] text-tier-reported font-sans">{photoError}</div>
            ) : null}
            {photoConfirmation ? (
              <div className="text-[13px] text-tier-verified font-sans">{photoConfirmation}</div>
            ) : null}

            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={photoStatus === 'uploading' || photoStatus === 'processing'}
            >
              {photoStatus === 'done' ? 'Close' : 'Cancel'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
